// @ts-strict-ignore
import * as React from 'react';
import {Button, Typography} from 'onboarding-components';
import moment from 'moment';
import copy from 'clipboard-copy';
import produce from 'immer';
import take from 'lodash/take';

import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {TEXT_HIGHLIGHT_THEME, TextHighlightReact} from 'common/components/text-highlight';
import {IMMERSIVE_ASSETS} from 'common/onboarding/immersive-onboarding-legacy/assets';
import {Content, Header} from 'common/onboarding/immersive-onboarding-legacy/components';
import {reporter} from 'common/report/error-reporting';
import {appApiUrl, handleJsonRequest} from 'common/report/queries/utils';
import {apiClient} from 'common/util/api-client';
import {Banner} from 'common/onboarding/immersive-onboarding-legacy/components/banner';
import {CheckIntegrationResult, SetupGuideContext, Step} from 'common/onboarding/immersive-onboarding-legacy/types';
import {useMountEffect} from 'common/onboarding/utils';
import {
  SetUpTrackingEvent,
  track,
  TrackingEvent,
  TrackingProperty,
  trackSetUp,
} from 'common/onboarding/immersive-onboarding-legacy/tracking';
import {ApiClient} from 'common/onboarding/immersive-onboarding-legacy/api-client';
import {TooltipReact} from 'common/components/tooltip';
import {Placement, TooltipTextAlign} from 'common/components/tooltip/types';
import {useCurrentUser} from 'common/contexts/react/current-user-context';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {getLocation} from 'common/util/url';
import {Trans} from 'common/i18n';
import {useTranslation} from 'common/i18n/hooks';

import textHighlightStyleOverride from './textHighlight.inline.styl';
import styles from './implement-phase2.module.styl';
import {DuplicateIcon} from './duplicate-icon';
import {EventListener} from './event-listener';

type ImplementMethodProps = {
  title: string;
  url: string;
  imgSrc: string;
  highlight?: string;
};

const MAX_OWNER_DISPLAY_COUNT = 4;

const ImplementMethod = (props: ImplementMethodProps) => {
  return (
    <a
      className={styles.implementMethod}
      href={props.url}
      target='_blank'
      onClick={() => {
        trackSetUp(SetUpTrackingEvent.LinkClicked, {cta: props.title, [TrackingProperty.Phase2]: true});
      }}
    >
      <div className={styles.implementMethodIcon}>
        <img src={props.imgSrc} />
      </div>
      <div className={styles.implementMethodTextContainer}>
        <Typography variant='captionXs' weight='bold' className={styles.implementMethodText}>
          {props.title}
        </Typography>
        {props.highlight && (
          <TextHighlightReact
            style-override={textHighlightStyleOverride}
            size='small'
            theme={TEXT_HIGHLIGHT_THEME.SUCCESS}
            text={props.highlight}
          />
        )}
      </div>
    </a>
  );
};

export const Implement = () => {
  const {formatList, t} = useTranslation();
  const {completed, isIntegrated, setIsIntegrated, setupGuideInfo, setSetupGuideInfo, setStep} =
    React.useContext(SetupGuideContext);
  const currentUser = useCurrentUser();
  const currentProject = useCurrentProject();
  const [isCopying, setIsCopying] = React.useState<boolean>(false);
  const [owners, setOwners] = React.useState([]);

  const pollingRef = React.useRef(null);
  const paths = getLocation().pathname.split(`/`);
  const projectId = paths[paths.indexOf(`project`) + 1];

  const getProjectIntegrationStatus = async () => {
    try {
      const res: CheckIntegrationResult = await apiClient.get(appApiUrl(`check-integration`, {projectId}));
      if (res.is_integrated) {
        if (setupGuideInfo) {
          const updatedStepsCompleted = produce(setupGuideInfo.stepsCompleted, (draft) => draft.add(Step.Implement));

          ApiClient.updateCompleted(setupGuideInfo.projectId, updatedStepsCompleted);

          setSetupGuideInfo({...setupGuideInfo, stepsCompleted: updatedStepsCompleted});
        }
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      setIsIntegrated(res.is_integrated);
      trackSetUp(SetUpTrackingEvent.IntegratedWithinSession, {[TrackingProperty.Phase2]: true});
    } catch (error) {
      reporter.error(error);
    }
  };

  const getOwnersAccount = async () => {
    try {
      const {results} = await handleJsonRequest(
        fetch(`/organizations/${currentProject.get()?.value.organizationId}/users`, {
          method: `GET`,
        }),
      );
      setOwners(
        results
          .filter((user) => user.org_role === `owner` || user.org_role === `admin`)
          .sort((a, b) => b.last_activity - a.last_activity),
      );
    } catch {
      throw new Error(`Error retrieving users`);
    }
  };

  const previouslyIntegrated = completed[Step.Implement] || isIntegrated;
  useMountEffect(() => {
    if (pollingRef.current === null && !previouslyIntegrated) {
      pollingRef.current = window.setInterval(getProjectIntegrationStatus, 10000);
    }
    getOwnersAccount();
    return () => {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    };
  });

  const copyToken = async (token: string) => {
    try {
      await copy(token);
      setIsCopying(true);
      setTimeout(() => {
        setIsCopying(false);
      }, 1500);
      trackSetUp(SetUpTrackingEvent.TokenCopied, {[TrackingProperty.Phase2]: true});
    } catch (err) {
      reporter.error(err);
      addToast({
        type: ToastType.ERROR,
        message: t(`onboarding.implement.copy-token-error`),
      });
    }
  };

  const getOwnersString = () => {
    let ownersEmail = take(owners, MAX_OWNER_DISPLAY_COUNT).reduce((acc, user) => (acc += `${user.email}\n`), ``);
    const numRemainingOwners = owners.slice(MAX_OWNER_DISPLAY_COUNT).length;
    ownersEmail +=
      numRemainingOwners > 0
        ? t(`onboarding.implement.remaining-owners`, {remainingOwners: owners.slice(MAX_OWNER_DISPLAY_COUNT).length})
        : ``;
    return ownersEmail;
  };

  const getOrgName = () => {
    const ordId = currentProject.get()?.value.organizationId;
    return currentUser.get()?.value?.organizations[ordId]?.name;
  };

  const ProjectToken = () => {
    let tokenText: string;
    if (isCopying) {
      tokenText = t(`onboarding.implement.copied`);
    } else if (setupGuideInfo?.projectToken) {
      tokenText = setupGuideInfo.projectToken;
    }

    return (
      <div className={styles.tokenContainer}>
        <div className={styles.tokenHeader}>
          <Typography variant='captionXs'>{t(`onboarding-legacy.implement.copy-token`)}</Typography>
          <div className={styles.infoTooltip}>
            <img src={IMMERSIVE_ASSETS.help} className={styles.infoIcon} />
            <TooltipReact
              placement={Placement.Top}
              text={t(`onboarding-legacy.implement.token-tooltip`)}
              maxWidth={`200px`}
            />
          </div>
        </div>
        <button
          className={styles.copyTokenButton}
          onClick={() => !isCopying && copyToken(`${setupGuideInfo.projectToken}`)}
          disabled={!tokenText}
        >
          {/* hide the full token in code to prevent unauth access. text-overflow will ellipsify */}
          <div className={styles.tokenText}>{tokenText ? tokenText.slice(0, 12) : `*********`}</div>
          <DuplicateIcon className={styles.duplicateIcon} />
          {!tokenText && (
            <TooltipReact
              placement={Placement.Bottom}
              text={t(`onboarding-legacy.implement.no-permission-tooltip`, {
                orgName: getOrgName(),
                ownersString: getOwnersString(),
              })}
              maxWidth={`250px`}
              textAlign={TooltipTextAlign.Start}
            />
          )}
        </button>
      </div>
    );
  };

  const eventList = (events) => {
    if (!events || events.length === 0) {
      return ``;
    }
    if (events.length === 1) {
      return events[0];
    }
    return formatList(events);
  };

  const EventBanner = () => {
    if (!setupGuideInfo?.eventsPropertiesMap) {
      return null;
    }

    const lastEditedUser =
      currentUser.get().value?.email === setupGuideInfo?.email ? t(`onboarding.implement.you`) : setupGuideInfo?.author;
    const lastEditedDate = setupGuideInfo?.modified ? moment(setupGuideInfo.modified).format(`MMM D, YYYY`) : ``;
    const plannedEvents = Object.keys(setupGuideInfo?.eventsPropertiesMap) ?? [];
    return (
      <Banner isVisible={plannedEvents.length > 0}>
        <Banner.Content
          title={t(`onboarding-legacy.implement.planned-events`, {eventList: eventList(plannedEvents)})}
          description={t(`onboarding-legacy.implement.last-edited`, {lastEditedUser, lastEditedDate})}
        />
        <Banner.Action
          action={() => {
            setStep(Step.Events);
            track(TrackingEvent.EditEvent, {[TrackingProperty.Phase2]: true});
          }}
          actionName={t(`onboarding-legacy.implement.view-planned-events`)}
        />
      </Banner>
    );
  };

  const ImplementCdpCTA = () => {
    return (
      <div className={styles.implementSection}>
        <Typography variant='bodySm' weight='bold'>
          {t(`onboarding-legacy.implement.implemented-cdp`)}
        </Typography>
        <div className={styles.implementMethodsGrid}>
          <ImplementMethod
            title={`Segment`}
            url={`https://developer.mixpanel.com/docs/segment`}
            imgSrc={IMMERSIVE_ASSETS.segment}
            highlight={t(`onboarding-legacy.implement.popular`)}
          />
          <ImplementMethod
            title={`BigQuery`}
            url={`https://docs.mixpanel.com/docs/tracking/data-warehouse?dwh=bigquery`}
            imgSrc={IMMERSIVE_ASSETS.bq}
          />
          <ImplementMethod
            title={`Snowflake`}
            url={`https://docs.mixpanel.com/docs/tracking-methods/data-warehouse?dwh=snowflake`}
            imgSrc={IMMERSIVE_ASSETS.snowflake}
          />
          <ImplementMethod
            title={`RudderStack`}
            url={`https://developer.mixpanel.com/docs/rudderstack`}
            imgSrc={IMMERSIVE_ASSETS.rudderstack}
          />
          <ImplementMethod
            title={`Redshift`}
            url={`https://docs.mixpanel.com/docs/tracking-methods/data-warehouse?dwh=redshift`}
            imgSrc={IMMERSIVE_ASSETS.redshift}
          />
          <ImplementMethod
            title={t(`onboarding-legacy.implement.more-options`)}
            url={`https://docs.mixpanel.com/docs/tracking-methods/choosing-the-right-method`}
            imgSrc={IMMERSIVE_ASSETS.more}
          />
        </div>
      </div>
    );
  };

  const ImplementSdkCTA = () => {
    return (
      <div className={styles.implementSection}>
        <Typography variant='bodySm' weight='bold'>
          {t(`onboarding-legacy.implement.first-analytics-tool`)}
        </Typography>
        <Typography variant='captionXs'>
          <Trans
            i18nKey={`onboarding.implement.sdk-includes-autocapture`}
            t={t}
            components={{
              docLink: <a href='https://docs.mixpanel.com/docs/tracking-methods/autocapture' target='_blank' />,
            }}
          />
        </Typography>
        <div className={styles.implementMethodsGrid}>
          <ImplementMethod
            title={`JavaScript`}
            url={`https://developer.mixpanel.com/docs/javascript-quickstart`}
            imgSrc={IMMERSIVE_ASSETS.js}
            highlight={t(`onboarding-legacy.implement.popular`)}
          />
          <ImplementMethod
            title={`Android`}
            url={`https://developer.mixpanel.com/docs/android-quickstart`}
            imgSrc={IMMERSIVE_ASSETS.android}
            highlight={t(`onboarding-legacy.implement.popular`)}
          />
          <ImplementMethod
            title={`iOS`}
            url={`https://developer.mixpanel.com/docs/ios-swift-quickstart`}
            imgSrc={IMMERSIVE_ASSETS.ios}
            highlight={t(`onboarding-legacy.implement.popular`)}
          />
          <ImplementMethod
            title={`React Native`}
            url={`https://developer.mixpanel.com/docs/react-native-quickstart`}
            imgSrc={IMMERSIVE_ASSETS.reactNative}
          />
          <ImplementMethod
            title={`Google Tag Manager`}
            url={`https://docs.mixpanel.com/docs/tracking/integrations/google-tag-manager`}
            imgSrc={IMMERSIVE_ASSETS.gtm}
          />
          <ImplementMethod
            title={t(`onboarding-legacy.implement.more-options`)}
            url={`https://docs.mixpanel.com/docs/tracking-methods/choosing-the-right-method`}
            imgSrc={IMMERSIVE_ASSETS.more}
          />
        </div>
      </div>
    );
  };

  const SectionButton = () => {
    const {modalManager} = React.useContext(SetupGuideContext);
    return (
      <div className={styles.implementFooterButton}>
        <Button
          disabled={!previouslyIntegrated}
          onClick={() => {
            modalManager.closeModal();
            track(TrackingEvent.CloseModal, {[TrackingProperty.Phase2]: true});
          }}
        >
          {t(`onboarding-legacy.implement.finish-and-close`)}
        </Button>
      </div>
    );
  };

  return (
    <>
      <Header
        title={t(`onboarding-legacy.implement.connect-data-header`)}
        step={t(`onboarding-legacy.implement.step-three`)}
        duration={15}
        imgSrc={IMMERSIVE_ASSETS.sendData}
        completed={completed[Step.Implement]}
      />
      <Content>
        <div className={styles.implementHeader}>
          <div>
            <Typography className={styles.headerText} variant='headingSm'>
              {t(`onboarding-legacy.implement.choose-connection-method`)}
            </Typography>
            <Typography className={styles.subheaderText} variant='bodySm'>
              <Trans
                i18nKey={`onboarding.implement.implementation-guide`}
                t={t}
                components={{
                  guideLink: (
                    <a
                      href='https://docs.mixpanel.com/docs/tracking-methods/choosing-the-right-method'
                      target='_blank'
                    />
                  ),
                }}
              />
            </Typography>
          </div>
          <ProjectToken />
        </div>
        <ImplementCdpCTA />
        <ImplementSdkCTA />
        <EventListener isIntegrated={previouslyIntegrated} />
        <EventBanner />
        <SectionButton />
      </Content>
    </>
  );
};
